import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useGetMany } from "react-admin";
import type { AppliedSchemaMapping } from "../../hooks/useAppliedFieldMapping";
import { SectionCard } from "@/components/common/SectionCard";
import { ENTITY_CONFIG } from "../result-tab/config";
import { useClaimantLookup } from "../result-tab/hooks";
import { AppliedMappingTable } from "./AppliedMappingTable";
import { buildSampleValueLookup } from "./buildSampleValueLookup";

interface SchemaMappingCardProps {
  schemaMapping: AppliedSchemaMapping;
  /** Entity ID of the first loaded record for this schema (if any). When
   *  provided, the card fetches that entity and renders a sample-value
   *  column on the mapping table. Pass `undefined` to render the table
   *  without samples (e.g. no load yet). */
  sampleEntityId?: string;
}

/**
 * Per-schema mapping card on the Mapping tab.
 *
 * Renders a SectionCard header with the schema label and "N fields mapped"
 * summary, then the AppliedMappingTable extended with a "Sample (row 1)"
 * column. Sample values come from fetching the first loaded record of the
 * schema and reading each mapped field off the resolved entity, formatted
 * through the same `formatFieldValue` pipeline the Result tab uses.
 */
export const SchemaMappingCard = ({ schemaMapping, sampleEntityId }: SchemaMappingCardProps) => {
  const config = ENTITY_CONFIG[schemaMapping.schema];
  const { mappings } = schemaMapping;
  const ids = sampleEntityId ? [sampleEntityId] : [];

  const { data: entities, isLoading } = useGetMany<{ id: string } & Record<string, unknown>>(
    config.resource,
    { ids },
    { enabled: ids.length > 0 }
  );

  const sampleEntity = entities?.[0] ?? null;

  // Build a format hint per target field by indexing the result-tab config —
  // keeps sample formatting consistent (currency, dates, claimant refs).
  // Fields not present in the schema's result-tab config fall through to
  // "text" inside buildSampleValueLookup.
  const formatByTargetField = useMemo(() => {
    const map: Record<string, NonNullable<(typeof config.fields)[number]["format"]>> = {};
    for (const f of config.fields) {
      if (f.format) map[f.field] = f.format;
    }
    return map;
  }, [config.fields]);

  const claimantLookup = useClaimantLookup(sampleEntity ? [sampleEntity] : []);

  const sampleValueLookup = useMemo(
    () =>
      buildSampleValueLookup({
        entity: sampleEntity,
        mappings,
        formatByTargetField,
        claimantLookup,
      }),
    [sampleEntity, mappings, formatByTargetField, claimantLookup]
  );

  // When the load is empty or the fetch failed entirely, every sample value
  // resolves to "—" — `buildSampleValueLookup` handles that for us, but the
  // column would read as a long row of dashes which adds visual noise
  // without information. In that case, skip the sample column entirely.
  const hasAnySample = Object.values(sampleValueLookup).some((v) => v !== "—");
  const subtitle = `${mappings.length} field${mappings.length !== 1 ? "s" : ""} mapped`;

  return (
    <SectionCard title={config.labelPlural} subtitle={subtitle} compact noPadding>
      <Box sx={{ px: 1.5, py: 1 }}>
        {isLoading && sampleEntityId ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            Loading sample values…
          </Typography>
        ) : (
          <AppliedMappingTable mappings={mappings} sampleValueLookup={hasAnySample ? sampleValueLookup : undefined} />
        )}
      </Box>
    </SectionCard>
  );
};
